import styles from "./home2.module.scss";
import { useEffect, useRef, useState } from "react";
import { TypeAnimation } from "react-type-animation";
import BlurAnimate from "../company/animations/BlurToVisible";
import "./animation.css";

const HIW_STEPS = [
  { n: "1", title: "Customer Reaches Out",  desc: "WhatsApp, web, or app, wherever your customer already is.",             tag: "AI"    },
  { n: "2", title: "AI Detects Intent",     desc: "Routes to the right flow instantly, no manual triage needed.",          tag: "AI"    },
  { n: "3", title: "AI Qualifies",          desc: "Asks questions, reads documents, builds a complete lead profile.",       tag: "AI"    },
  { n: "4", title: "AI Books",              desc: "Schedules the site visit or appointment automatically.",                  tag: "AI"    },
  { n: "5", title: "Warm Lead Delivered",   desc: "Verified, summarised, and ready, handed directly to your team.",        tag: "AGENT" },
  { n: "6", title: "Your Team Closes",      desc: "With full context already done, just show up and close.",               tag: "AGENT" },
];

function HIWCarousel() {
  const [active, setActive] = useState(0);
  const covRef = useRef(null);

  useEffect(() => {
    let t = null;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        t = setInterval(() => setActive(p => (p + 1) % HIW_STEPS.length), 3200);
      } else {
        clearInterval(t);
        t = null;
      }
    }, { threshold: 0.1 });
    if (covRef.current) observer.observe(covRef.current);
    return () => { clearInterval(t); observer.disconnect(); };
  }, []);

  const go = (dir) => setActive(p => (p + dir + HIW_STEPS.length) % HIW_STEPS.length);

  return (
    <div className={styles.hiw__cov} ref={covRef}>
      <div className={styles.hiw__track}>
        {HIW_STEPS.map((s, i) => {
          let off = i - active;
          if (off > HIW_STEPS.length / 2) off -= HIW_STEPS.length;
          if (off < -HIW_STEPS.length / 2) off += HIW_STEPS.length;
          if (Math.abs(off) > 2) return null;
          const absOff = Math.abs(off);
          const scale   = [1, 0.78, 0.62][absOff];
          const tx      = off * 320;
          const opacity = [1, 0.65, 0.32][absOff];
          return (
            <div
              key={s.n}
              className={styles.hiw__cov__card}
              data-active={String(absOff === 0)}
              style={{ transform: `translateX(${tx}px) scale(${scale})`, opacity, zIndex: 10 - absOff }}
              onClick={() => setActive(i)}
            >
              <div className={`${styles.hiw__circle} ${s.tag === "AGENT" ? styles.hiw__circle__agent : ""}`}>{s.n}</div>
              <div className={styles.hiw__step__tag} data-type={s.tag === "AGENT" ? "Agent" : "AI"}>{s.tag}</div>
              <div className={styles.hiw__cov__title}>{s.title}</div>
              {absOff === 0 && <div className={styles.hiw__cov__desc}>{s.desc}</div>}
            </div>
          );
        })}
      </div>
      <div className={styles.hiw__nav}>
        <button className={styles.hiw__arrow} onClick={() => go(-1)}>‹</button>
        <div className={styles.hiw__dots}>
          {HIW_STEPS.map((_, i) => (
            <div key={i} className={i === active ? styles.hiw__dot__active : styles.hiw__dot} onClick={() => setActive(i)} />
          ))}
        </div>
        <button className={styles.hiw__arrow} onClick={() => go(1)}>›</button>
      </div>
    </div>
  );
}

function HeroGrid() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId, w, h;
    let paused = false;

    const resize = () => {
      w = canvas.width  = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const observer = new IntersectionObserver(([entry]) => {
      paused = !entry.isIntersecting;
      if (!paused) { cancelAnimationFrame(animId); animId = requestAnimationFrame(draw); }
    }, { threshold: 0 });
    observer.observe(canvas);

    const CELL = 68;

    function makePacket() {
      const horiz = Math.random() > 0.35;
      return {
        horiz,
        line:  Math.random(),
        pos:   -0.08 - Math.random() * 0.1,
        speed: 0.00035 + Math.random() * 0.00055,
        rgb:   Math.random() > 0.55 ? [124, 58, 237] : [56, 139, 253],
        len:   0.05 + Math.random() * 0.07,
        size:  2 + Math.random() * 1.5,
      };
    }

    const PACK_COUNT = 18;
    const packs = Array.from({ length: PACK_COUNT }, makePacket);

    const glowNodes = Array.from({ length: 9 }, () => ({
      colFrac: Math.random(),
      rowFrac: Math.random(),
      phase:   Math.random() * Math.PI * 2,
      speed:   0.012 + Math.random() * 0.016,
    }));

    let t = 0;

    const draw = () => {
      if (paused) return;
      t++;
      ctx.clearRect(0, 0, w, h);

      const cols = Math.ceil(w / CELL) + 1;
      const rows = Math.ceil(h / CELL) + 1;

      // Grid lines
      ctx.lineWidth = 0.5;
      for (let c = 0; c <= cols; c++) {
        const x = c * CELL;
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h);
        ctx.strokeStyle = 'rgba(56,139,253,0.09)'; ctx.stroke();
      }
      for (let r = 0; r <= rows; r++) {
        const y = r * CELL;
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y);
        ctx.strokeStyle = 'rgba(56,139,253,0.09)'; ctx.stroke();
      }

      // All intersection nodes — subtle pulse
      for (let c = 0; c <= cols; c++) {
        for (let r = 0; r <= rows; r++) {
          const x = c * CELL, y = r * CELL;
          const pulse = 0.5 + 0.5 * Math.sin(t * 0.013 + c * 0.71 + r * 1.23);
          ctx.beginPath();
          ctx.arc(x, y, 1.2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(88,166,255,${0.18 + pulse * 0.28})`;
          ctx.fill();
        }
      }

      // Special bright glowing nodes
      glowNodes.forEach(n => {
        n.phase += n.speed;
        const x = Math.round(n.colFrac * cols) * CELL;
        const y = Math.round(n.rowFrac * rows) * CELL;
        const pulse = 0.5 + 0.5 * Math.sin(n.phase);
        const r2 = 18 * pulse + 6;
        const grd = ctx.createRadialGradient(x, y, 0, x, y, r2);
        grd.addColorStop(0, `rgba(88,166,255,${0.45 * pulse})`);
        grd.addColorStop(1, 'rgba(88,166,255,0)');
        ctx.beginPath(); ctx.arc(x, y, r2, 0, Math.PI * 2);
        ctx.fillStyle = grd; ctx.fill();
        ctx.beginPath(); ctx.arc(x, y, 2.5 + pulse, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200,230,255,${0.55 + pulse * 0.45})`; ctx.fill();
      });

      // Data packets flowing along lines
      packs.forEach((p, idx) => {
        p.pos += p.speed;
        if (p.pos > 1 + p.len) { packs[idx] = makePacket(); return; }
        const [r, g, b] = p.rgb;
        if (p.horiz) {
          const lineRow = Math.round(p.line * rows);
          const y = lineRow * CELL;
          const hx = p.pos * w, tx = (p.pos - p.len) * w;
          const grd = ctx.createLinearGradient(tx, y, hx, y);
          grd.addColorStop(0, `rgba(${r},${g},${b},0)`);
          grd.addColorStop(1, `rgba(${r},${g},${b},0.75)`);
          ctx.beginPath(); ctx.moveTo(tx, y); ctx.lineTo(hx, y);
          ctx.strokeStyle = grd; ctx.lineWidth = 1.5; ctx.stroke();
          ctx.beginPath(); ctx.arc(hx, y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${r},${g},${b},0.9)`; ctx.fill();
        } else {
          const lineCol = Math.round(p.line * cols);
          const x = lineCol * CELL;
          const hy = p.pos * h, ty = (p.pos - p.len) * h;
          const grd = ctx.createLinearGradient(x, ty, x, hy);
          grd.addColorStop(0, `rgba(${r},${g},${b},0)`);
          grd.addColorStop(1, `rgba(${r},${g},${b},0.75)`);
          ctx.beginPath(); ctx.moveTo(x, ty); ctx.lineTo(x, hy);
          ctx.strokeStyle = grd; ctx.lineWidth = 1.5; ctx.stroke();
          ctx.beginPath(); ctx.arc(x, hy, p.size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${r},${g},${b},0.9)`; ctx.fill();
        }
      });

      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); observer.disconnect(); };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }}
    />
  );
}

const CAPABILITY_CARDS = [
  { icon: "📱", label: "WhatsApp-Native",                   desc: "Meet customers where they already are. No downloads, no friction.", accent: "#0ea5e9" },
  { icon: "📄", label: "Document Reading (OCR)",            desc: "Customers upload a bill or ID, Sowiz extracts the data. No forms needed.", accent: "#10b981" },
  { icon: "🎥", label: "Video Responses Inside the Chatbot", desc: "Customers get answers as short videos, not walls of text. Higher trust, higher conversion.", accent: "#7c3aed" },
  { icon: "🧠", label: "Social Wisdom AI",                  desc: "Real reviews and social proof surfaced at the exact moment someone is deciding.", accent: "#1f6feb" },
];

const WHATIS_CARDS = [
  { icon: "🏢", label: "For Businesses",     desc: "Automate your entire pre-sales funnel. More qualified leads, less manual work.",      accent: "#1f6feb", accentRgb: "31,111,235"   },
  { icon: "👤", label: "For Your Customers", desc: "Instant answers on WhatsApp or web, no forms, no hold music.",                       accent: "#7c3aed", accentRgb: "124,58,237"  },
  { icon: "📊", label: "For Investors",      desc: "Proven B2B2C traction in a $290B conversational commerce market.",                    accent: "#10b981", accentRgb: "16,185,129"  },
];

const FOR_ROLES_TABS = [
  {
    icon: "🎯", label: "Business Leaders",
    features: [
      { icon: "💰", title: "More pipeline, less overhead",       desc: "Your team stops doing discovery. They start closing. The AI handles the top of your funnel 24/7, at zero marginal cost per conversation." },
      { icon: "⚡", title: "Go live in days, not months",        desc: "No long implementation cycles. Sowiz connects to your existing CRM and channels. Most clients qualify leads within two weeks of signing." },
      { icon: "🔮", title: "Every conversation is a data point", desc: "See exactly what customers are asking, where they drop off, and what’s converting. Make decisions with real signal, not guesses." },
    ],
    card: { label: "GET STARTED", title: "See your ROI in 30 minutes", desc: "We’ll walk you through the exact flow for your business and show you what results to expect within 90 days.", cta: "Book a Demo →" },
  },
  {
    icon: "🔧", label: "Tech Teams",
    features: [
      { icon: "🔌", title: "REST API + Webhooks + SDK", desc: "Full programmatic control. Integrate Sowiz into your existing stack in hours, not weeks." },
      { icon: "🔗", title: "Native connectors",         desc: "Salesforce, HubSpot, Zoho, Freshdesk, WhatsApp Business API, plug in and go." },
      { icon: "🔒", title: "Enterprise-grade security", desc: "AES-256 encryption · RBAC · Full audit logs · GDPR-compliant. 99.9% uptime · Sub-1.5s response latency." },
    ],
    card: { label: "FOR DEVELOPERS", title: "Explore the API docs", desc: "Everything you need to integrate, extend, and build on Sowiz, in one place.", cta: "View API Docs →" },
  },
  {
    icon: "💰", label: "Investors",
    features: [
      { icon: "📈", title: "$290B market. Proven traction.", desc: "3M+ end customers across live deployments in energy, insurance, and financial services." },
      { icon: "🏗️", title: "Clear moat, defensible model",  desc: "B2B2C model: revenue scales with client customer base. No single competitor has all four capabilities." },
      { icon: "🚀", title: "Ready to scale",                desc: "Integrated video + WhatsApp + OCR + social proof, built for complex, high-value sales cycles." },
    ],
    card: { label: "INVESTOR RELATIONS", title: "Request the investor deck", desc: "Full financials, traction metrics, and market analysis available on request.", cta: "Request Investor Deck →" },
  },
];

const VERTICALS_DATA = [
  { icon: "☀️", name: "Energy & Solar",      desc: "Bill collection, survey scheduling, subsidy qualification. Automated." },
  { icon: "🏦", name: "Finance & Insurance", desc: "KYC, policy FAQs, advisor routing. Compliant and fast." },
  { icon: "🏥", name: "Healthcare",          desc: "Booking, triage, insurance pre-auth. No hold music." },
  { icon: "🛍",  name: "E-commerce",         desc: "WhatsApp support, order updates, referral campaigns. All automated." },
];

function ForRoles({ onBookDemo }) {
  const [tab, setTab] = useState(0);
  const cur = FOR_ROLES_TABS[tab];
  const bodyRef = useRef(null);
  const cardRef = useRef(null);
  const featuresRef = useRef(null);
  const playRef = useRef(null);

  useEffect(() => {
    const body = bodyRef.current;
    const card = cardRef.current;
    const featCont = featuresRef.current;
    if (!body || !card || !featCont) return;

    const reset = () => {
      card.style.transition = 'none';
      card.style.opacity = '0';
      card.style.transform = 'translateX(70px)';
      featCont.querySelectorAll('[data-feature]').forEach(el => {
        el.style.transition = 'none';
        el.style.opacity = '0';
        el.style.transform = 'translateX(-70px)';
      });
    };

    const play = () => {
      featCont.querySelectorAll('[data-feature]').forEach((el, i) => {
        el.style.transition = 'none';
        el.style.opacity = '0';
        el.style.transform = 'translateX(-70px)';
        setTimeout(() => {
          el.style.transition = `opacity 0.65s cubic-bezier(0.22,1,0.36,1) ${i*0.15}s, transform 0.65s cubic-bezier(0.22,1,0.36,1) ${i*0.15}s`;
          el.style.opacity = '1';
          el.style.transform = 'translateX(0)';
        }, 30);
      });
      card.style.transition = 'none';
      card.style.opacity = '0';
      card.style.transform = 'translateX(70px)';
      setTimeout(() => {
        card.style.transition = 'opacity 0.65s cubic-bezier(0.22,1,0.36,1) 0.15s, transform 0.65s cubic-bezier(0.22,1,0.36,1) 0.15s';
        card.style.opacity = '1';
        card.style.transform = 'translateX(0)';
      }, 30);
    };

    playRef.current = play;
    reset();
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) play(); else reset();
    }, { threshold: 0.1 });
    obs.observe(body);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (playRef.current) playRef.current();
  }, [tab]);

  return (
    <div className={styles.forroles}>
      <div className={styles.forroles__tabs}>
        {FOR_ROLES_TABS.map((t, i) => (
          <button key={i} className={i === tab ? styles.forroles__tab__active : styles.forroles__tab} onClick={() => setTab(i)}>
            <span className={styles.forroles__tab__icon}>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>
      <div className={styles.forroles__body} ref={bodyRef}>
        <div className={styles.forroles__features} ref={featuresRef}>
          {cur.features.map((f, i) => (
            <div key={i} className={styles.forroles__feature} data-feature>
              <div className={styles.forroles__feature__icon}>{f.icon}</div>
              <div className={styles.forroles__feature__content}>
                <div className={styles.forroles__feature__title}>{f.title}</div>
                <div className={styles.forroles__feature__desc}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div className={styles.forroles__card} ref={cardRef}>
          <div className={styles.forroles__card__label}>{cur.card.label}</div>
          <div className={styles.forroles__card__title}>{cur.card.title}</div>
          <div className={styles.forroles__card__desc}>{cur.card.desc}</div>
          <button className={styles.btn__primary} onClick={() => onBookDemo && onBookDemo()}>{cur.card.cta}</button>
        </div>
      </div>
    </div>
  );
}

const STATS_DATA = [
  { num: 6,   suffix: "M+", label: "Customers Engaged via WhatsApp",          color: "#58a6ff" },
  { num: 178, suffix: "K+", label: "AI Conversations, Zero Human Involvement", color: "#a78bfa" },
  { num: 60,  suffix: "%+", label: "Reduction in Cost-per-Qualified-Lead",     color: "#34d399" },
];

const PETAL_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];

function StatItem({ num, suffix, label, color }) {
  const [display, setDisplay]   = useState(0);
  const [active,  setActive]    = useState(false);
  const [bloom,   setBloom]     = useState(false);
  const ref    = useRef(null);
  const rafRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setDisplay(0);
        setBloom(false);
        setActive(false);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          setActive(true);
          setBloom(true);
        }, 100);
      } else {
        clearTimeout(timerRef.current);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        setActive(false);
        setBloom(false);
        setDisplay(0);
      }
    }, { threshold: 0.45 });
    obs.observe(el);
    return () => { obs.disconnect(); clearTimeout(timerRef.current); };
  }, []);

  useEffect(() => {
    if (!active) return;
    let start = null;
    const duration = 2200;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(ease * num));
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [active, num]);

  return (
    <div className={styles.stats__item} ref={ref}>
      <div className={styles.stats__glow} style={{ '--glow-color': color }} />
      {bloom && (
        <div className={styles.stats__flower}>
          <div className={styles.stats__ring1} style={{ '--ring-color': color }} />
          <div className={styles.stats__ring2} style={{ '--ring-color': color }} />
          {PETAL_ANGLES.map((angle, i) => (
            <div key={i} className={styles.stats__petal}
              style={{ '--angle': `${angle}deg`, '--color': color, '--delay': `${i * 0.06}s` }} />
          ))}
        </div>
      )}
      <span className={styles.stats__num} style={{ color }}>
        {display}{suffix}
      </span>
      <div className={styles.stats__bar} style={{ '--bar-color': color }} />
      <span className={styles.stats__label}>{label}</span>
    </div>
  );
}

const PROBLEM_CARDS = [
  { icon: "⚡", title: "Slow Response = Lost Leads", desc: "78% of customers buy from whoever responds first. Every minute of delay costs you deals.", accent: "#f59e0b", glow: "rgba(245,158,11,0.35)" },
  { icon: "📉", title: "Unqualified Pipeline", desc: "60% of sales time goes to leads that never convert. Your team is busy, not productive.", accent: "#ef4444", glow: "rgba(239,68,68,0.35)" },
  { icon: "🤖", title: "Repetitive Work", desc: "FAQs, paperwork, scheduling, none of it needs a human. Yet your team does it anyway.", accent: "#8b5cf6", glow: "rgba(139,92,246,0.35)" },
];

// Slide In Left · Fade Up + Zoom · Slide In Right
const PROBLEM_ENTRY = [
  { from: 'translateX(-38px)',           delay: 0    },
  { from: 'translateY(32px) scale(0.96)', delay: 0.15 },
  { from: 'translateX(38px)',            delay: 0.30 },
];

function ProblemGrid() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const wraps = Array.from(el.querySelectorAll('[data-card-wrap]'));

    const reset = () => wraps.forEach((w, i) => {
      w.style.transition = 'none';
      w.style.opacity    = '0';
      w.style.transform  = PROBLEM_ENTRY[i]?.from || 'translateY(30px)';
    });

    const play = () => wraps.forEach((w, i) => {
      const { from, delay } = PROBLEM_ENTRY[i] || { from: 'translateY(30px)', delay: i * 0.15 };
      w.style.transition = 'none';
      w.style.opacity    = '0';
      w.style.transform  = from;
      setTimeout(() => {
        w.style.transition = `opacity 0.7s ease-out ${delay}s, transform 0.7s ease-out ${delay}s`;
        w.style.opacity    = '1';
        w.style.transform  = 'none';
      }, 30);
    });

    reset();
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) play(); else reset();
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className={styles.problem__cards} ref={ref}>
      {PROBLEM_CARDS.map((c, i) => (
        <div key={i} data-card-wrap>
          <div className={styles.problem__card} style={{ '--accent': c.accent, '--glow': c.glow }}>
            <div className={styles.problem__card__icon}>{c.icon}</div>
            <div className={styles.problem__card__title}>{c.title}</div>
            <div className={styles.problem__card__desc}>{c.desc}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

const CAP_PETAL_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];

// Each card blooms from its corner — like 4 petals opening from center
const CAP_ENTRY = [
  { from: 'translate(-80px,-80px) scale(0.25)', delay: 0    },
  { from: 'translate( 80px,-80px) scale(0.25)', delay: 0.14 },
  { from: 'translate(-80px, 80px) scale(0.25)', delay: 0.08 },
  { from: 'translate( 80px, 80px) scale(0.25)', delay: 0.22 },
];

function CapCard({ icon, label, desc, accent, index }) {
  const [bloom, setBloom] = useState(false);
  const wrapRef  = useRef(null);
  const timerRef = useRef(null);
  const entry    = CAP_ENTRY[index] || CAP_ENTRY[0];

  useEffect(() => {
    const wrap = wrapRef.current; if (!wrap) return;

    const reset = () => {
      wrap.style.transition = 'none';
      wrap.style.transform  = entry.from;
      wrap.style.opacity    = '0';
      setBloom(false);
    };

    reset();

    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        reset();
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          wrap.style.transition = `transform 0.9s cubic-bezier(0.22,1,0.36,1) ${entry.delay}s, opacity 0.7s ease ${entry.delay}s`;
          wrap.style.transform  = 'translate(0,0) scale(1)';
          wrap.style.opacity    = '1';
          setTimeout(() => setBloom(true), (entry.delay + 0.25) * 1000);
        }, 30);
      } else {
        clearTimeout(timerRef.current);
        reset();
      }
    }, { threshold: 0.2 });

    obs.observe(wrap);
    return () => { obs.disconnect(); clearTimeout(timerRef.current); };
  }, [index]);

  return (
    <div ref={wrapRef}>
      <div className={styles.cap__card} style={{ '--accent': accent }}>
        <div className={styles.cap__icon__wrap}>
          {bloom && (
            <div className={styles.cap__flower}>
              <div className={styles.cap__ring1} style={{ '--ring-color': accent }} />
              <div className={styles.cap__ring2} style={{ '--ring-color': accent }} />
              {CAP_PETAL_ANGLES.map((angle, i) => (
                <div key={i} className={styles.cap__petal}
                  style={{ '--angle': `${angle}deg`, '--color': accent, '--delay': `${i * 0.06}s` }} />
              ))}
            </div>
          )}
          <div className={styles.cap__card__icon}>{icon}</div>
        </div>
        <div className={styles.cap__card__body}>
          <div className={styles.cap__card__title}>{label}</div>
          <div className={styles.cap__card__desc}>{desc}</div>
        </div>
      </div>
    </div>
  );
}

function useDropReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(-60px)';
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        el.style.animation = 'none';
        void el.offsetWidth;
        el.style.opacity = '';
        el.style.transform = '';
        el.style.animation = 'productDropIn 0.85s cubic-bezier(0.22,1,0.36,1) forwards';
      } else {
        el.style.animation = 'none';
        el.style.opacity = '0';
        el.style.transform = 'translateY(-60px)';
      }
    }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function useProductReveal(isReverse) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const video = el.querySelector('video');
    const detail = Array.from(el.children).find(c => c.tagName === 'DIV');
    const phoneAnim = isReverse ? 'phoneEnterFromLeft' : 'phoneEnterFromRight';

    const reset = () => {
      if (video)  { video.style.animation = 'none';  video.style.opacity = '0'; }
      if (detail) { detail.style.animation = 'none'; detail.style.opacity = '0'; }
    };
    reset();

    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        if (video) {
          video.style.animation = `${phoneAnim} 0.9s cubic-bezier(0.22,1,0.36,1) forwards`;
          setTimeout(() => {
            video.style.opacity = '1';
            video.style.animation = 'phoneFloat 3.5s ease-in-out infinite';
          }, 900);
        }
        if (detail) detail.style.animation = 'textReveal 0.8s cubic-bezier(0.22,1,0.36,1) 0.55s forwards';
      } else {
        reset();
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [isReverse]);
  return ref;
}

function useEntryAnim(entryClass, showClass) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const items = Array.from(el.children);
    items.forEach((item, i) => { item.classList.add(styles[entryClass]); item.style.setProperty('--i', i); });
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) { items.forEach(item => item.classList.add(styles[showClass])); obs.disconnect(); } });
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

export default function HomeV2() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const wisRef  = useRef(null);
  useEffect(() => {
    const grid = wisRef.current; if (!grid) return;
    const cards = Array.from(grid.children);
    const reset = () => cards.forEach(c => { c.style.transition = 'none'; c.style.opacity = '0'; c.style.transform = 'translateX(80px)'; });
    const play  = () => cards.forEach((c, i) => {
      c.style.transition = 'none'; c.style.opacity = '0'; c.style.transform = 'translateX(80px)';
      setTimeout(() => {
        c.style.transition = `opacity 0.7s cubic-bezier(0.22,1,0.36,1) ${i*0.18}s, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${i*0.18}s`;
        c.style.opacity = '1'; c.style.transform = 'translateX(0)';
      }, 30);
    });
    reset();
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) play(); else reset(); }, { threshold: 0.1 });
    obs.observe(grid);
    return () => obs.disconnect();
  }, []);
  const vertRef = useRef(null);
  useEffect(() => {
    const grid = vertRef.current; if (!grid) return;
    const cards = Array.from(grid.children);
    const reset = () => cards.forEach(c => { c.style.transition = 'none'; c.style.opacity = '0'; c.style.transform = 'translateX(-80px)'; });
    const play  = () => cards.forEach((c, i) => {
      c.style.transition = 'none'; c.style.opacity = '0'; c.style.transform = 'translateX(-80px)';
      setTimeout(() => {
        c.style.transition = `opacity 0.6s cubic-bezier(0.22,1,0.36,1) ${i*0.12}s, transform 0.6s cubic-bezier(0.22,1,0.36,1) ${i*0.12}s`;
        c.style.opacity = '1'; c.style.transform = 'translateX(0)';
      }, 30);
    });
    reset();
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) play(); else reset(); }, { threshold: 0.1 });
    obs.observe(grid);
    return () => obs.disconnect();
  }, []);

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', company: '', phone: '', email: '', message: "Hi Sowiz, I'd like to book a product demo for my business." });

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent('Book a Demo - ' + (form.company || form.name));
    const body = encodeURIComponent(
      `Name: ${form.name}\nCompany: ${form.company}\nPhone: ${form.phone}\nEmail: ${form.email}\n\nMessage:\n${form.message}`
    );
    window.open(`https://mail.google.com/mail/?view=cm&to=contact@sowiz.live&su=${subject}&body=${body}`);
    setShowModal(false);
    setForm({ name: '', company: '', phone: '', email: '', message: "Hi Sowiz, I'd like to book a product demo for my business." });
  };

  const drop1 = useProductReveal(false);
  const drop2 = useProductReveal(true);
  const drop3 = useProductReveal(false);
  const drop4 = useProductReveal(true);

  return (
    <div className={styles.layout}>

      {/* ── BOOK A DEMO MODAL ── */}
      {showModal && (
        <div className={styles.modal__overlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <button className={styles.modal__close} onClick={() => setShowModal(false)}>✕</button>
            <div className={styles.modal__badge}>Book a Demo</div>
            <h2 className={styles.modal__title}>Let's show you what Sowiz can do</h2>
            <p className={styles.modal__sub}>Fill in your details and we'll get back to you within 24 hours.</p>
            <form className={styles.modal__form} onSubmit={handleSubmit}>
              <div className={styles.modal__row}>
                <div className={styles.modal__field}>
                  <label>Full Name *</label>
                  <input required placeholder="Your name" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} />
                </div>
                <div className={styles.modal__field}>
                  <label>Company Name *</label>
                  <input required placeholder="Your company" value={form.company} onChange={e => setForm(f => ({...f, company: e.target.value}))} />
                </div>
              </div>
              <div className={styles.modal__row}>
                <div className={styles.modal__field}>
                  <label>Phone Number *</label>
                  <input required placeholder="+91 00000 00000" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} />
                </div>
                <div className={styles.modal__field}>
                  <label>Email *</label>
                  <input required type="email" placeholder="you@company.com" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} />
                </div>
              </div>
              <div className={styles.modal__field}>
                <label>Message</label>
                <textarea rows={3} value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))} />
              </div>
              <button type="submit" className={styles.modal__submit}>Send Request →</button>
            </form>
          </div>
        </div>
      )}

      {/* Global background rings — poore page pe */}
      <div className={styles.ring1} />
      <div className={styles.ring2} />
      <div className={styles.ring3} />
      <div className={styles.aurora1} />
      <div className={styles.aurora2} />

      {/* ── HEADER ── */}
      <header className={styles.header}>
        <div className={styles.header__logo}>
          <img src="/logoname.png" alt="Sowiz" />
        </div>
        <nav className={styles.header__nav}>
          <a href="#how-it-works">How It Works</a>
          <a href="#solutions">Solutions</a>
          <a href="#features">For Developers</a>
          <a href="#case-study">Case Studies</a>
        </nav>
        <button className={styles.header__cta} onClick={() => setShowModal(true)}>Book a Demo</button>
      </header>

      {/* ── HERO ── */}
      <section className={styles.hero}>
        {/* aurora3 only in hero */}
        <div className={styles.aurora3} />
        <HeroGrid />

        {/* Left */}
        <div className={styles.hero__left}>
          <div className={styles.hero__badge}>
            <span className={styles.hero__badge__dot} />
            AI-Powered Sales Automation
          </div>

          <h1 className={styles.hero__title}>
            <TypeAnimation
              sequence={[
                'Close More Deals.',      1800,
                'Qualify More Leads.',    1800,
                'Convert on WhatsApp.',   1800,
                'Book More Site Visits.', 1800,
              ]}
              wrapper="span"
              speed={55}
              deletionSpeed={70}
              repeat={Infinity}
              cursor={true}
            />
            <span className={styles.hero__title__accent}>Automate the Journey.</span>
          </h1>

          <p className={styles.hero__desc}>
            AI that qualifies leads, answers questions on WhatsApp, and hands your
            team sales-ready opportunities, 24/7.
          </p>

          <div className={styles.hero__actions}>
            <button className={styles.btn__primary} onClick={() => setShowModal(true)}>→ Book a Demo</button>
            <button className={styles.btn__secondary} onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}>See How It Works</button>
          </div>

        </div>

      </section>



      {/* ── PROBLEM ── */}
      <BlurAnimate className={styles.problem}>
        <div className={styles.problem__top}>
          <div className={styles.problem__label}>The Problem</div>
          <div className={styles.problem__title}>
            <span className={styles.problem__title__white}>Your team's time is valuable.</span>
            <span className={styles.problem__title__blue}>They shouldn't answer the same question 200 times a day.</span>
          </div>
        </div>
        <ProblemGrid />
        <div className={styles.problem__bar}>Sowiz handles all of it, so your team only touches leads that are ready to close.</div>
      </BlurAnimate>

      {/* ── SERVICES ── */}
      <BlurAnimate className={styles.services} id="features">
        <div className={styles.section__label}>What Makes Us Different</div>
        <div className={styles.title}>Four Capabilities. No Single Competitor Has All Four.</div>
        <div className={styles.cap__cards}>
          {CAPABILITY_CARDS.map((c, i) => (
            <CapCard key={i} icon={c.icon} label={c.label} desc={c.desc} accent={c.accent} index={i} />
          ))}
        </div>
      </BlurAnimate>

      {/* ── HOW IT WORKS ── */}
      <BlurAnimate className={styles.hiw} id="how-it-works">
        <div className={styles.hiw__title}>How It <em>Works</em></div>
        <div className={styles.hiw__sub}>Customer asks → AI qualifies → Your team closes</div>
        <HIWCarousel />
      </BlurAnimate>

      {/* ── STATS ── */}
      <BlurAnimate className={styles.stats}>
        <div className={styles.stats__grid}>
          {STATS_DATA.map((s, i) => (
            <>
              <StatItem key={i} {...s} />
              {i < STATS_DATA.length - 1 && <div key={`d${i}`} className={styles.stats__divider} />}
            </>
          ))}
        </div>
      </BlurAnimate>

      {/* ── PRODUCTS ── */}
      <BlurAnimate className={styles.products} id="solutions">
        <div className={styles.section__label}>Tailored Solutions</div>
        <div className={styles.title}>Built for Your Business Challenges</div>
        <div className={styles.subtitle}>Innovative answers that drive real results.</div>

        <div className={styles.product} ref={drop1}>
          <video className={styles.product__img} autoPlay loop muted playsInline><source src="/utl.web.mp4" type="video/mp4" /></video>
          <div className={styles.product__detail}>
            <div className={styles.product__detail__title}>
              AI-Powered Social Wisdom: <strong>Compiled Video Summaries</strong>
            </div>
            <div className={styles.product__detail__desc}>
              Harness AI-driven Social Wisdom to deliver video summaries that capture key insights from social media trends, providing customers with concise and relevant content, saving time and enabling smarter decisions.
            </div>
          </div>
        </div>

        <div className={`${styles.product} ${styles.product__reverse}`} ref={drop2}>
          <video className={styles.product__img} autoPlay loop muted playsInline><source src="/chat.web.mp4" type="video/mp4" /></video>
          <div className={`${styles.product__detail} ${styles.product__detail__reverse}`}>
            <div className={styles.product__detail__title}>
              Interactive Chatbots with <strong>Videos to Answer the Question</strong>
            </div>
            <div className={styles.product__detail__desc}>
              Our interactive chatbots offer effective and personalized interactions. Customers are far more likely to be convinced by well-crafted videos within the chatbot, clear messaging that elevates the overall user experience.
            </div>
          </div>
        </div>

        <div className={styles.product} ref={drop3}>
          <video className={styles.product__img} autoPlay loop muted playsInline><source src="/referal.web.mp4" type="video/mp4" /></video>
          <div className={styles.product__detail}>
            <div className={styles.product__detail__title}>
              Interactive Referral System for <strong>Building Trust</strong>
            </div>
            <div className={styles.product__detail__desc}>
              Our AI-powered referral system taps into trusted connections, boosting conversion rates by helping customers find deals within their network.
            </div>
          </div>
        </div>

        <div className={`${styles.product} ${styles.product__reverse}`} ref={drop4}>
          <video className={styles.product__img} autoPlay loop muted playsInline><source src="/subsidy.web.mp4" type="video/mp4" /></video>
          <div className={`${styles.product__detail} ${styles.product__detail__reverse}`}>
            <div className={styles.product__detail__title}>
              Uncovering Hidden Insights with <strong>AI-Powered Data</strong>
            </div>
            <div className={styles.product__detail__desc}>
              Our AI-driven approach identifies customer pain points, uncovers inefficiencies, and delivers targeted hyper-personalized follow-ups that transform insights into actionable strategies.
            </div>
          </div>
        </div>
      </BlurAnimate>

      {/* ── FOR ROLES ── */}
      <BlurAnimate className={styles.forroles__section}>
        <div className={styles.section__label}>WHO IT'S FOR</div>
        <div className={styles.title}>Built for Your Role</div>
        <ForRoles onBookDemo={() => setShowModal(true)} />
      </BlurAnimate>

      {/* ── BUILT FOR EVERY STAKEHOLDER ── */}
      <BlurAnimate className={styles.verticals__section}>
        <div className={styles.section__label}>BUILT FOR</div>
        <div className={styles.title}>Built for Every Stakeholder</div>
        <div className={styles.whatis__cards} ref={wisRef} style={{ marginTop: '3rem' }}>
          {WHATIS_CARDS.map((c, i) => (
            <div key={i} className={styles.whatis__card} style={{ '--accent': c.accent, '--accent-rgb': c.accentRgb }}>
              <div className={styles.whatis__card__icon}>{c.icon}</div>
              <div className={styles.whatis__card__label}>{c.label}</div>
              <div className={styles.whatis__card__desc}>{c.desc}</div>
            </div>
          ))}
        </div>
      </BlurAnimate>

      {/* ── VERTICALS ── */}
      <BlurAnimate className={styles.verticals__section}>
        <div className={styles.section__label}>Industries We Serve</div>
        <div className={styles.title}>Designed for Complex, High-Value Sales</div>
        <div className={styles.verticals__grid} ref={vertRef}>
          {VERTICALS_DATA.map((v, i) => (
            <div key={i} className={styles.vertical__card}>
              <div className={styles.vertical__icon}>{v.icon}</div>
              <div className={styles.vertical__name}>{v.name}</div>
              <div className={styles.vertical__desc}>{v.desc}</div>
            </div>
          ))}
        </div>
      </BlurAnimate>

      {/* ── FINAL CTA ── */}
      <BlurAnimate className={styles.bottomcta}>
        <div className={styles.bottomcta__box}>
          <div className={styles.bottomcta__title}>See it in action. 30 minutes.</div>
          <div className={styles.bottomcta__sub}>No commitments. Just a clear look at what Sowiz can do for your business.</div>
          <div className={styles.bottomcta__actions}>
            <button className={styles.bottomcta__btn} onClick={() => setShowModal(true)}>→ Book a Demo</button>
          </div>
        </div>
      </BlurAnimate>

      {/* ── FOOTER ── */}
      <footer className={styles.footer}>
        <div className={styles.footer__top}>
          <div className={styles.footer__brand}>
            <div className={styles.footer__logo}>
              <img src="/logo192.png" alt="Sowiz" />
              <span>SOWIZ</span>
            </div>
            <p>AI that automates your customer journey from first message to closed deal.</p>
          </div>
          <div className={styles.footer__col}>
            <h4>COMPANY</h4>
            <a onClick={() => { window.location.href = "/career"; }}>Careers</a>
            <a onClick={() => { window.location.href = "/terms"; }}>Terms & Conditions</a>
            <a onClick={() => { window.location.href = "/privacy"; }}>Privacy Policy</a>
            <a onClick={() => { window.location.href = "/investors"; }}>Investors</a>
          </div>
        </div>
        <div className={styles.footer__bottom}>
          <span>© 2026 Sowiz. All Rights Reserved. &nbsp;|&nbsp; v2.1.0</span>
          <div className={styles.footer__socials}>
            <a className={styles.footer__social} href="https://www.linkedin.com/company/sowiz-inc/" target="_blank" rel="noreferrer">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            <a className={styles.footer__social} href="https://mail.google.com/mail/?view=cm&to=contact@sowiz.live&su=Book a Demo - Sowiz" target="_blank" rel="noreferrer">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
              </svg>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
